import React, {
  useState,
  DragEvent,
  ChangeEvent,
  useEffect,
  forwardRef,
} from "react";
import axios from "axios";
import { Chip } from "@mui/material";
import { global, globalMenu } from "@shared/libs/Global";
import { fnGetDataSet } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";

interface SnFileUploadBoxProps {
  selectedDocu: string;
  selectedLineDocu: number;
  selectedDtDocu?: string;
  /** 업로드 성공 후 서버 응답(id, nm_file) 목록을 넘긴다. 선택 옵션 */
  onUploaded?: (files: any[]) => void | Promise<void>;
  /** 삭제 성공 후 삭제된 파일을 넘긴다. 선택 옵션 */
  onDeleted?: (file: UploadedFile) => void | Promise<void>;
}

interface UploadedFile {
  id: number;
  nm_file: string;
}

const SnFileUploadBox = forwardRef(
  (
    {
      selectedDocu,
      selectedLineDocu,
      selectedDtDocu,
      onUploaded,
      onDeleted,
    }: SnFileUploadBoxProps,
    ref
  ) => {
    const { userInfo } = global();
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const cdMenu = globalMenu.getState().activeMenuInfo.cd_menu || "";
    const cdModule = globalMenu.getState().activeMenuInfo.cd_module || "";

    useEffect(() => {
      const searchFileList = async () => {
        // 문서번호가 없으면(신규 등) 조회하지 않는다. 업로드(handleDrop)와 동일한 가드로,
        // 빈 no_docu로 조회해 다른 문서의 파일이 섞여 나오는 것을 막는다.
        if (!selectedDocu) {
          setFiles([]);
          return;
        }

        const params = {
          id: "com_attachfile_01",
          params: {
            cd_firm: userInfo.cd_firm,
            cd_menu: cdMenu,
            no_docu: selectedDocu,
            no_line_docu: selectedLineDocu,
          },
        };

        const result: any = await fnGetDataSet(params);
        if (result[0] && result[0].length > 0) {
          setFiles(result[0]);
        } else {
          setFiles([]);
        }
      };
      searchFileList();
    }, [selectedDocu, selectedLineDocu]);

    // 화면 콜백 오류가 업로드·삭제 실패로 보이지 않게 분리한다
    const notify = async (fn: () => void | Promise<void>) => {
      try {
        await fn();
      } catch (e) {
        console.error("첨부파일 콜백 실패:", e);
      }
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => e.preventDefault();

    const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();

      if (!selectedDocu) {
        await showDialog(
          "alter",
          "신규 등록 시 파일을 업로드할 수 없습니다. 저장 후 진행해 주시기 바랍니다."
        );
        return;
      }

      if (e.dataTransfer.files.length > 0) uploadFiles(e.dataTransfer.files);
    };

    const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        uploadFiles(e.target.files);
      }
    };

    const uploadFiles = async (fileList: FileList) => {
      try {
        const formData = new FormData();
        for (let i = 0; i < fileList.length; i++) {
          formData.append("files", fileList[i]);
        }
        formData.append("cdFirm", userInfo.cd_firm || "");
        formData.append("cdMenu", cdMenu);
        formData.append("noDocu", selectedDocu);
        formData.append("noLineDocu", selectedLineDocu.toString());
        formData.append("dtDocu", selectedDtDocu || "");
        formData.append("cdUser", userInfo.cd_user || "");
        formData.append("cdModule", cdModule);

        const res = await axios.post("/snotes/file/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
          },
        });
        if (res.data.result) {
          const if_status = res.data.result.if_status;
          if (if_status === "E") {
            throw new Error(res.data.result.message);
          }
        }

        if (Array.isArray(res.data)) {
          setFiles((prev) => {
            // 현재 파일명 목록
            const existingNames = new Set(prev.map((f) => f.id));
            // 신규 파일만 필터링
            const newFiles = res.data.filter(
              (f: any) => !existingNames.has(f.id)
            );
            return [...prev, ...newFiles];
          });
          await notify(() => onUploaded?.(res.data));
          await showDialog("success", "파일이 저장되었습니다.");
        }
      } catch (err) {
        console.error("업로드 실패:", err);
        await showDialog("error", "파일 업로드를 실패했습니다.");
      }
    };

    const handleDownload = async (file: UploadedFile) => {
      try {
        const res = await axios.post(
          "/snotes/file/download",
          { id: file.id, cdModule: cdModule },
          {
            headers: {
              "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
            },
            responseType: "blob",
          }
        );

        if (res.data.result) {
          const if_status = res.data.result.if_status;
          if (if_status === "E") {
            throw new Error(res.data.result.message);
          }
        }

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file.nm_file);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err: any) {
        // 404 = 서버 저장소(테넌트 경로·옛 경로)에 파일이 없음. 일반 오류와 구분해 안내한다.
        if (err?.response?.status === 404) {
          await showDialog("alter", "서버에 저장된 파일이 없습니다.");
          return;
        }
        console.error("파일 다운로드를 실패했습니다.");
        await showDialog("error", "파일 다운로드를 실패했습니다.");
      }
    };

    const handleDelete = async (file: UploadedFile) => {
      try {
        const confirm = await showDialog("confirm", "파일을 삭제하시겠습니까?");
        if (!confirm) return;

        const res = await axios.delete(
          `/snotes/file/delete/${cdModule}/${file.id}`,
          {
            withCredentials: true,
            headers: {
              "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
            },
          }
        );

        if (res.data.result) {
          const if_status = res.data.result.if_status;
          if (if_status === "E") {
            throw new Error(res.data.result.message);
          }
        }

        setFiles((prev) => prev.filter((f) => f.id !== file.id));
        await notify(() => onDeleted?.(file));
        await showDialog("success", "파일이 삭제되었습니다.");
      } catch (err: any) {
        // 404 = 서버 저장소(테넌트 경로·옛 경로)에 파일이 없음. 첨부 정보는 남겨둔 채 안내만 한다.
        if (err?.response?.status === 404) {
          await showDialog("alter", "서버에 저장된 파일이 없습니다.");
          return;
        }
        console.error("삭제 실패:", err);
        await showDialog("error", "파일 삭제를 실패했습니다.");
      }
    };

    return (
      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          border: "1px solid #babfc7",
          padding: "5px",
          textAlign: "center",
        }}
      >
        {files.length === 0 ? (
          <p
            style={{
              color: "gray",
              fontSize: 14,
              minHeight: 25,
              lineHeight: "25px",
            }}
          >
            파일을 마우스로 끌어 오세요
          </p>
        ) : (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
              justifyContent: "flex-start",
            }}
          >
            {files.map((file) => (
              <Chip
                key={file.id}
                label={file.nm_file}
                variant="outlined"
                onClick={() => handleDownload(file)}
                onDelete={() => handleDelete(file)}
                sx={{
                  height: "auto",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                  cursor: "pointer",
                  borderRadius: "3px",
                  "& .MuiChip-label": {
                    textDecoration: "underline",
                    paddingRight: "3px",
                    paddingLeft: "8px",
                  },
                  "& .MuiChip-deleteIcon": {
                    fontSize: "16px",
                    marginLeft: "4px",
                  },
                }}
              />
            ))}
          </div>
        )}

        {/* 숨겨진 input */}
        <input
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: "none" }}
          id="file-upload-input"
        />
      </div>
    );
  }
);

export default React.memo(SnFileUploadBox);
